import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * Free-text notes an admin keeps about a client: how they pay, what was
 * agreed, anything worth remembering next time. One box per person, edited in
 * place rather than a list of timestamped entries, because what you want a year
 * later is the current state of the arrangement, not its history.
 *
 * **The client never sees this.** The notes live on the user row but ride on
 * `UserAdminOut`, which only the admin-only /users routes return; `/me`, which
 * every role can call, still returns plain `UserOut`.
 */
export default function ClientNotesModal({ open, user, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setText(user?.notes || "");
      setErr(null);
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !user) return null;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      // "" clears the notes; the server turns a blank string back into null.
      const { data } = await api.patch(`/users/${user.id}`, { notes: text });
      onSaved?.(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save the notes");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 640 }}
      >
        <h3>Notes on {user.name}</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          Only admins see this. {user.name} never does, here or anywhere else in
          the app.
        </p>
        <textarea
          className="notes-box"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="How they pay, what was agreed, anything worth remembering next time."
          rows={10}
          autoFocus
        />
        {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save notes"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
